import React, { useState } from 'react';
import { allCars, categories } from '../data/mock';
import CarCard from './CarCard';
import CarDetailModal from './CarDetailModal';
import { ChevronDown } from 'lucide-react';

const FleetSection = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [visibleCount, setVisibleCount] = useState(12);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredCars = activeCategory === 'All'
    ? allCars
    : allCars.filter(car => car.category === activeCategory);

  const visibleCars = filteredCars.slice(0, visibleCount);
  const hasMore = visibleCount < filteredCars.length;

  const loadMore = () => setVisibleCount(prev => prev + 12);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setVisibleCount(12);
  };

  const handleCarClick = (car) => {
    setSelectedCar(car);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedCar(null), 300);
  };

  return (
    <section id="fleet" data-testid="fleet-section" className="py-20 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 animate-fadeInUp">
            Vehicles Ready for
            <span className="text-gold-gradient"> Rent</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto animate-fadeInUp animation-delay-100">
            Choose from our extensive collection of luxury and exotic vehicles
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12 animate-fadeInUp animation-delay-200">
          {categories.map((category, index) => (
            <button
              key={category}
              data-testid={`category-filter-${category.toLowerCase()}`}
              onClick={() => handleCategoryChange(category)}
              className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-medium transition-all duration-300 text-sm sm:text-base ${
                activeCategory === category
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black shadow-[0_0_20px_rgba(212,160,23,0.4)] scale-105'
                  : 'bg-gray-900/80 text-gray-400 hover:bg-gray-800 hover:text-white border border-gray-800 hover:border-amber-500/30'
              }`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Cars Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {visibleCars.map((car, index) => (
            <CarCard 
              key={car.id} 
              car={car} 
              index={index} 
              onCarClick={handleCarClick}
            />
          ))}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="text-center mt-12 animate-fadeInUp">
            <button
              data-testid="load-more-btn"
              onClick={loadMore}
              className="group inline-flex items-center gap-2 px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl border border-gray-800 hover:border-amber-500/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,160,23,0.2)]"
            >
              Load More Vehicles
              <span className="text-amber-400">({filteredCars.length - visibleCount} remaining)</span>
              <ChevronDown className="w-4 h-4 text-amber-400 group-hover:translate-y-1 transition-transform" />
            </button>
          </div>
        )}
      </div>

      {/* Car Detail Modal */}
      <CarDetailModal 
        car={selectedCar}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </section>
  );
};

export default FleetSection;
